/**
 * @name setScrollTrigger
 */
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
gsap.registerPlugin(ScrollTrigger);

/**
 * @name setScrollTrigger
 * @description 各ページのアニメーションを呼び出す
 */
export function setScrollTrigger() {
    setScrollTriggerAction();
};

/**
 * 初期値
 */
const html = document.documentElement;
let mm = gsap.matchMedia();
let props = {
    action: { duration: 1, ease: 'Quart.easeOut', overwrite: true, },
    trigger: { start: `top 65%`, end: `top 65%`, once: true },
    scrub: {
        start: `top bottom`,
        end: 'bottom top',
        scrub: 1.5,
        invalidateOnRefresh: true,
    }
}

/**
 * @name setScrollTriggerInit
 * @description 共通アニメーション
 * @param {string} getTrigger - トリガー要素
 * @param {string} type - アニメーションタイプ
 */
function setScrollTriggerInit(getTrigger, type = '') {

    // フェードイン
    const fadeIn = () => {
        let fadeIn__item = getTrigger.querySelectorAll('.--js_fadeIn__item');
        if (fadeIn__item.length > 0) {
            gsap.set(fadeIn__item, { opacity: 0, });
            ScrollTrigger.batch(getTrigger, {
                onEnter: () => gsap.to([fadeIn__item], { ...props.action, opacity: 1, stagger: 0.1, }),
                ...props.trigger,
                // markers: true,
            });
        }
    }

    // フェードアップ
    const fadeUp = () => {
        let fadeUp__item = getTrigger.querySelectorAll('.--js_fadeUp__item');
        if (fadeUp__item.length > 0) {
            gsap.set(fadeUp__item, { opacity: 0, y: "25%" });
            ScrollTrigger.batch(getTrigger, {
                onEnter: () => gsap.to([fadeUp__item], { ...props.action, opacity: 1, y: 0, stagger: 0.1, }),
                ...props.trigger,
                // markers: true,
            });
        }
    }

    // フェードダウン
    const fadeDown = () => {
        let fadeDown__item = getTrigger.querySelectorAll('.--js_fadeDown__item');
        if (fadeDown__item.length > 0) {
            gsap.set(fadeDown__item, { opacity: 0, y: "-50%" });
            ScrollTrigger.batch(getTrigger, {
                onEnter: () => gsap.to([fadeDown__item], { ...props.action, opacity: 1, y: 0, stagger: 0.2, }),
                ...props.trigger,
                // markers: true,
            });
        }
    }

    //スケールダウン
    const scaleDown = () => {
        let scaleDown__item = getTrigger.querySelectorAll('.--js_scaleDown__item');
        if (scaleDown__item.length > 0) {
            gsap.set(scaleDown__item, { scale: 1.1, opacity: 0, });
            ScrollTrigger.batch(getTrigger, {
                onEnter: () => gsap.to([scaleDown__item], { ...props.action, scale: 1, opacity: 1, stagger: 0.1, }),
                ...props.trigger,
                // markers: true,
            });
        }
    }

    //スケールアップ - X
    const scaleUpX = () => {
        let scaleUpX__item = getTrigger.querySelectorAll('.--js_scaleUpX__item');
        if (scaleUpX__item.length > 0) {
            gsap.set(scaleUpX__item, { scaleX: 0 });
            ScrollTrigger.batch(getTrigger, {
                onEnter: () => gsap.to([scaleUpX__item], { ...props.action, scaleX: 1, stagger: 0.1, }),
                ...props.trigger,
                // markers: true,
            });
        }
    }

    //clipPath - 右から左へ
    const clipPathRight = () => {
        let clipPathRight__item = getTrigger.querySelectorAll('.--js_clipPathRight__item');
        if (clipPathRight__item.length > 0) {
            gsap.set(clipPathRight__item, { clipPath: 'inset(0 0 0 100%)' });
            ScrollTrigger.batch(getTrigger, {
                onEnter: () => gsap.to([clipPathRight__item], { ...props.action, clipPath: 'inset(0)', stagger: 0.1, }),
                ...props.trigger,
                // markers: true,
            });
        }
    }

    // パララックス - 背景
    const parallaxElBg = () => {
        let rect = getTrigger.getBoundingClientRect();
        let triggerHeight = rect.height;
        let el = getTrigger.querySelectorAll('.--js_parallax__item__bg');
        if (el.length > 0) {
            let elHeight = el[0].clientHeight;
            mm.add("(min-width: 835px)", () => {
                gsap.to(el, {
                    y: `${-(elHeight - triggerHeight)}px`, ease: "none", scrollTrigger: { trigger: getTrigger, ...props.scrub }
                })
            })
        }
    }

    // パララックス - 要素を上に移動
    const parallaxElTop = () => {
        let rect = getTrigger.getBoundingClientRect();
        let triggerHeight = rect.height;
        let el = getTrigger.querySelectorAll('.--js_parallax__item__top');
        if (el.length > 0) {
            mm.add("(min-width: 835px)", () => {
                gsap.set(el, { y: `0` })
                gsap.to(el, { y: `${triggerHeight / 4}px`, ease: "none", scrollTrigger: { trigger: getTrigger, ...props.scrub } })
            })
        }
    }

    // パララックス - 要素を下に移動
    const parallaxElBottom = () => {
        let rect = getTrigger.getBoundingClientRect();
        let triggerHeight = rect.height;
        let el = getTrigger.querySelectorAll('.--js_parallax__item__bottom');
        if (el.length > 0) {
            mm.add("(min-width: 835px)", () => {
                gsap.set(el, { y: `0` })
                gsap.to(el, {
                    y: `-${triggerHeight / 4}px`, ease: "none", scrollTrigger: { trigger: getTrigger, ...props.scrub }
                })
            })
        }
    }

    // アニメーションのオプション
    type == 'fadeIn' && fadeIn();
    type == 'fadeUp' && fadeUp();
    type == 'fadeDown' && fadeDown();
    type == 'scaleDown' && scaleDown();
    type == 'scaleUpX' && scaleUpX();
    type == 'clipPathRight' && clipPathRight();
}

/**
 * @name setScrollTrigger__top
 * @description トップページ用アニメーション
 */
function setScrollTriggerAction() {
    let setTrigger = document.querySelectorAll('.--js_setTrigger');
    setTrigger.forEach((getTrigger) => {
        setScrollTriggerInit(getTrigger, 'fadeIn');
        setScrollTriggerInit(getTrigger, 'fadeUp');
        setScrollTriggerInit(getTrigger, 'scaleDown');
        setScrollTriggerInit(getTrigger, 'scaleUpX');
        setScrollTriggerInit(getTrigger, 'clipPathRight');
    });
}